import type { Operation } from '@/util/roles/generated'

/**
 * Named capabilities that group one or more guarded API operations. A user has
 * the capability if they can invoke every listed operation. Prefer capability
 * names in templates so intent is readable and semantic — raw operation names
 * remain usable through the same `useCan` API.
 */
export const CAPABILITIES = {
  manageDeploymentSettings: [
    'vrfsPost',
    'buildingsPost',
    'galvanickAppliancesPost',
    'networkPrefixesPost',
    'vlansPost',
  ],
  manageUsers: [
    'usersOrganizationsGet',
    'usersOrganizationsIdGet',
    'usersGroupsGet',
    'usersGroupPut',
    'usersGroupDelete',
  ],
  managePlatformSettings: ['customAssetAttributesPost', 'geminiPost'],
  manageNotificationsSettings: ['geminiPost'],
  manageVrfs: ['vrfsPost', 'vrfsPut', 'vrfsToTagsPost', 'vrfsToTagsDelete'],
  manageAssetTags: ['assetsTagsPost', 'assetsTagsPut', 'assetsTagsDelete'],
  manageAssetState: ['assetsAssetIdStatePatch'],
  manageBuildings: ['buildingsPost', 'buildingsPut'],
  manageAppliances: [
    'galvanickAppliancesPost',
    'galvanickAppliancesPut',
    'galvanickAppliancesLocalPrefixesPost',
    'galvanickAppliancesLocalPrefixesDelete',
  ],
  manageNetworkPrefixes: [
    'networkPrefixesPost',
    'networkPrefixesPut',
    'networkPrefixesToTagsPost',
    'networkPrefixesToTagsDelete',
  ],
  manageVlans: ['vlansPost', 'vlansPut'],
  manageCustomAssetAttributes: [
    'customAssetAttributesPost',
    'customAssetAttributesPut',
    'customAssetAttributesDelete',
  ],
  uploadPCAPs: ['pcapUploadsPresignPost', 'pcapUploadsProcessPost'],
  // proxy for the admin role for now
  viewNetworkEvents: ['usersGroupsGet'],
} as const satisfies Record<string, ReadonlyArray<Operation>>

export type Capability = keyof typeof CAPABILITIES
